<template>
    <dashlet-card :chipClickable="clientMapCount > 0"
                  :chipText="t_clientMapCount"
                  :page-href="privilegeManage ? '/system/client-status' : undefined"
                  :page-tooltip="t_page_tooltip_clientstatus"
                  :title="t_title"
                  @chipClick="fitCameraToBounds">

        <template #prependButton>
            <square-button :disabled="!googleMap || !cameraBounds"
                           :loading="!googleMap && !gmapInitError"
                           :tooltip-text="t_center_map"
                           flat
                           icon="fal fa-location-dot"
                           @click="fitCameraToBounds" />
        </template>

        <div ref="googleMap"
             style="width: 100%; height: 500px;">
            <div v-if="gmapInitError"
                 class="dashlet-message-container">
                {{ gmapInitError }}
            </div>
        </div>

        <div id="show-plan"
             aria-hidden="true"
             class="modal inmodal fade"
             data-backdrop="static"
             role="dialog"
             tabindex="-1">
            <div class="modal-dialog modal-xl">
                <div class="modal-content animated fadeInUp"
                     style="overflow: hidden; overflow: clip;">
                    <modal-header :title="`${t_plan} ${plan?.name ?? ''}`"
                                  @close="selectedLocationId = null" />
                    <location-plan v-if="selectedLocationId != null"
                                   ref="locationPlan"
                                   :locationId="selectedLocationId"
                                   @plan="plan = $event" />
                </div>
            </div>
        </div>
    </dashlet-card>
</template>

<script>
import { initGoogleMapsLibrary } from '../../../utility'
import DashletCard from './dashlet-card.vue'
import LocationInfoWindowComponent from './clientsMap/locationInfoWindow.vue'
import Vue from 'vue'
import LocationPlan from './clientsLocationPlan/locationPlan.vue'
import ModalHeader from '../../content/common/modal/modal-header.vue'
import googleMapSettingsStorageMixin from './clientsMap/googleMapSettingsStorageMixin'
import SquareButton from '../../content/common/squareButton.vue'

export default {
    components: { SquareButton, ModalHeader, LocationPlan, DashletCard },
    mixins: [googleMapSettingsStorageMixin],
    props: {
        /** @type {'1' | undefined} */
        privilegeManage: String,
        googleMapsApiKey: String,

        tClientCount: {
            type: String,
            required: true
        },
        locations: {
            type: Array,
            default: () => []
        },
        mapData: {
            type: [Object, Array],
            default: () => {
            }
        },
        defaultCenter: {
            type: Object,
            default: () => {
            }
        }
    },
    data() {
        return {
            project_id: Laravel.activeProject,

            googleMap: undefined,
            infoWindow: undefined,
            infoWindowComponentInstance: undefined,
            openInfoWindowName: undefined,

            gmapInitError: false,

            selectedLocationId: null,
            plan: [],

            cameraBounds: null,

            t_title: Lang.langGet('Client Map'),
            t_page_tooltip_clientstatus: Lang.langGet('Client Status'),
            t_center_map: Lang.langGet('Center Map'),

            t_more: Lang.langGet('more'),
            t_close: Lang.langGet('Close'),
            t_plan: Lang.langGet('Plan for location')
        }
    },
    computed: {
        /* https://developers.google.com/maps/documentation/javascript/overview#MapOptions */
        mapOptions() {
            return {
                mapId: '1549486457dfbd6',
                minZoom: 1,

                streetViewControl: false,
                mapTypeControlOptions: {
                    style: google.maps.MapTypeControlStyle.DROPDOWN_MENU
                }
            }
        },
        clientMapCount() {
            return Object.values(this.mapData).reduce((accumulator, currentValue) => {
                return accumulator + currentValue?.clients.length ?? 0
            }, 0)
        },
        t_clientMapCount() {
            return `${this.clientMapCount} / ${this.tClientCount}`
        }
    },
    methods: {
        async initGoogleMap() {
            try {
                const { Map } = await google.maps.importLibrary('maps')
                this.googleMap = new Map(this.$refs.googleMap, this.mapOptions)
            } catch (e) {
                this.gmapInitError = e.message
            }
        },
        onInfoWindowClosed() {
            this.openInfoWindowName = undefined
            this.infoWindowComponentInstance?.$destroy()
        },
        showLocationMarkers() {
            this.cameraBounds = null

            if (Array.isArray(this.mapData)) return

            this.cameraBounds = new google.maps.LatLngBounds()

            Object.entries(this.mapData).forEach(([name, data]) => {

                let iconColor

                if (data.inactive !== data.players) {

                    if (data.connect_failures.errors > 0) {
                        iconColor = 'red'
                    } else {
                        if (data.fatals > 0) {
                            iconColor = 'red'
                        } else if (data.errors > 0) {
                            iconColor = 'orange'
                        } else if (data.warnings > 0) {
                            iconColor = 'yellow'
                        } else {
                            iconColor = 'green'
                        }

                        if (data.connect_failures.warnings > 0 && data.errors === 0) {
                            iconColor = 'orange'
                        }
                    }
                } else {
                    iconColor = 'grey'
                }

                const marker = new google.maps.Marker({
                    map: this.googleMap,
                    position: {
                        lat: +data.latitude,
                        lng: +data.longitude
                    },
                    title: `${data.name} [${data.players}]`,
                    icon: `/img/location-dot-solid-${iconColor}.svg`
                })

                this.cameraBounds.extend(marker.position)

                marker.addListener('click', () => {
                    if (this.openInfoWindowName !== name) {
                        this.infoWindow.close()
                        this.onInfoWindowClosed()

                        this.openInfoWindowName = name

                        /* https://stackoverflow.com/a/50197706 */
                        const LocationInfoWindow = Vue.extend(LocationInfoWindowComponent)
                        this.infoWindowComponentInstance = new LocationInfoWindow({
                            propsData: {
                                data,
                                name,
                                computedIconColor: iconColor
                            }
                        })

                        this.infoWindowComponentInstance.$on('showPlan', this.showPlan)

                        this.infoWindowComponentInstance.$mount()

                        this.infoWindow.setContent(this.infoWindowComponentInstance.$el)
                    }
                    this.infoWindow.open(this.googleMap, marker)
                })
            })
        },
        showPlan(locationId) {
            this.selectedLocationId = locationId
            EaseEventBus.fire('showModal', 'show-plan')
        },
        fitCameraToBounds() {
            if (!this.googleMap || !this.cameraBounds || this.cameraBounds.isEmpty()) return

            this.googleMap.fitBounds(this.cameraBounds, 64)
            const zoom = this.googleMap.getZoom()
            if (zoom && zoom > 16) this.googleMap.setZoom(16)
        }
    },
    created() {
        initGoogleMapsLibrary(this.googleMapsApiKey)
    },
    async mounted() {
        await this.initGoogleMap()

        if (this.gmapInitError) return

        // Create an info window to share between markers.
        this.infoWindow = new google.maps.InfoWindow()

        google.maps.event.addListener(this.infoWindow, 'closeclick', this.onInfoWindowClosed)

        this.showLocationMarkers()
    }
}
</script>
